// Math helpers for spherical-coordinate ↔ 3D conversions and clamping.
// Yaw is rotation around the Y (up) axis, in degrees, 0 = +Z (towards user-default view).
// Pitch is elevation, in degrees, +90 = up, -90 = down.

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;
export const radToDeg = (rad: number): number => (rad * 180) / Math.PI;

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function wrap360(deg: number): number {
  let v = deg % 360;
  if (v < 0) v += 360;
  return v;
}

export interface Vec3Lite {
  x: number;
  y: number;
  z: number;
}

/**
 * Convert spherical (yaw, pitch in degrees) → cartesian world position on a sphere
 * of `distance`. Output is calibrated so that a hotspot placed at (yaw, pitch) appears
 * at the centre of the screen when CameraControls reports the same (yaw, pitch).
 *
 * Why the negated x and z: PlayCanvas's camera "forward" is its local -Z axis.
 * After Y-axis rotation by `yaw`, the world-space forward is (-sin yaw, 0, -cos yaw).
 * For a hotspot to appear in that direction from origin, its position must sit on the
 * SAME ray — so position = (-sin yaw, 0, -cos yaw) * distance. Without the negation the
 * hotspot lands on the opposite side of the sphere and gets clipped as "behind camera".
 */
export function yawPitchToVec3(yawDeg: number, pitchDeg: number, distance = 1): Vec3Lite {
  const yaw = degToRad(yawDeg);
  const pitch = degToRad(pitchDeg);
  const cosPitch = Math.cos(pitch);
  return {
    x: -Math.sin(yaw) * cosPitch * distance,
    y: Math.sin(pitch) * distance,
    z: -Math.cos(yaw) * cosPitch * distance,
  };
}

export function vec3ToYawPitch(x: number, y: number, z: number): { yaw: number; pitch: number } {
  const distance = Math.sqrt(x * x + y * y + z * z) || 1;
  const pitch = Math.asin(clamp(y / distance, -1, 1));
  // Inverse of yawPitchToVec3: yaw = atan2(-x, -z) so that round-trip is identity.
  const yaw = Math.atan2(-x, -z);
  return { yaw: radToDeg(yaw), pitch: radToDeg(pitch) };
}
